// The editor's phone layout (spec docs/specs/editor/phone-layout.md): its viewport, its bottom
// tabs, the chosen clip's actions and where a sheet settles. Pure, so the layout's rules are
// tested without a browser; `PhoneLayout.tsx` draws them.
import type { Clip } from "./timeline";

/** The phone layout's viewport (decision 1): the app shell's phone width, upright. */
export const PHONE = "(max-width: 48em) and (orientation: portrait)";

/** A panel shown as a bottom sheet: a tab's, the chosen clip's Parameters, or the bell's list. */
export type SheetName =
  | "media"
  | "text"
  | "captions"
  | "transitions"
  | "effects"
  | "assistant"
  | "parameters"
  | "notifications";

/** The bottom tabs, the card's, in its order. */
export const PHONE_TABS: readonly { sheet: SheetName; label: string }[] = [
  { sheet: "media", label: "Media" },
  { sheet: "text", label: "Text" },
  { sheet: "captions", label: "Captions" },
  { sheet: "transitions", label: "Transitions" },
  { sheet: "effects", label: "Effects" },
  { sheet: "assistant", label: "Assistant" },
];

/** A sheet's title: its tab's name, "Parameters" or the bell's "Notifications". */
export function sheetTitle(sheet: SheetName): string {
  if (sheet === "parameters") return "Parameters";
  if (sheet === "notifications") return "Notifications";
  return PHONE_TABS.find((tab) => tab.sheet === sheet)?.label ?? sheet;
}

export type PhoneAction =
  | "done"
  | "split"
  | "speed"
  | "volume"
  | "duplicate"
  | "delete"
  | "parameters"
  | "captions"
  | "more";

/**
 * The chosen's actions on a phone, which replace the tabs: Done, which clears the choice and
 * brings the tabs back (decision 9), the card's Split, Speed, Volume, Duplicate and Delete, then
 * Parameters and More (decision 2). Speed and Volume are for videos and
 * sounds alone. A caption chosen has its Captions panel in Parameters' place, as the desktop hides
 * Parameters for one and opens its bin's Captions tab. A transition has its Delete and Parameters.
 * Whether each one may run now is its handler's to say, as on the desktop's toolbar.
 */
export function phoneActions(kinds: readonly Clip["kind"][], transition: boolean): readonly PhoneAction[] {
  if (transition) return ["done", "delete", "parameters"];
  const sounding = kinds.length > 0 && kinds.every((kind) => kind === "video" || kind === "audio");
  const captioned = kinds.includes("caption");
  return [
    "done",
    "split",
    ...(sounding ? (["speed", "volume"] as const) : []),
    "duplicate",
    "delete",
    captioned ? "captions" : "parameters",
    "more",
  ];
}

export type SheetHeight = "half" | "full";

/**
 * Where a sheet settles when its handle is let go after a drag of `dy` pixels (up is negative):
 * the nearest of closed, half and full height to where the drag left it.
 */
export function sheetStop(from: SheetHeight, dy: number, halfPx: number, fullPx: number): SheetHeight | "closed" {
  const at = (from === "half" ? halfPx : fullPx) - dy;
  const stops = [
    ["closed", 0],
    ["half", halfPx],
    ["full", fullPx],
  ] as const;
  let best: (typeof stops)[number] = stops[0];
  for (const stop of stops) if (Math.abs(stop[1] - at) < Math.abs(best[1] - at)) best = stop;
  return best[0];
}
